import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/Sidebar';
import TopNav from '../../components/TopNav';
import { adminService, enrollmentService } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { usePrompt } from '../../contexts/PromptContext';
import { Card, Badge, Button, Modal, Input, Field, Select, Textarea, EmptyState, Spinner, StatCard, Pagination } from '../../components/ui';
import TeacherAccountingModal from '../../components/TeacherAccountingModal';
import { CURRENCY_OPTIONS, formatPrice } from '../../utils/currency';

const STATUS_OPTIONS = ['pending', 'completed', 'failed', 'refunded'];
const EMPTY_FORM = { enrollment: '', amount: '', currency: 'USD', payment_method: 'cash', status: 'completed', notes: '' };
const PAGE_SIZE = 20;

function AdminPayments() {
  const { addNotification } = useNotification();
  const promptReason = usePrompt();
  const { t } = useTranslation();

  const [payments, setPayments] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [summary, setSummary] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [accountingTeacher, setAccountingTeacher] = useState(null);

  const fetchPayments = useCallback(() => {
    setLoading(true);
    const params = { page, page_size: PAGE_SIZE };
    if (statusFilter) params.status = statusFilter;
    adminService.getPayments(params)
      .then((res) => {
        const data = res.data;
        setPayments(Array.isArray(data) ? data : data.results || []);
        setCount(Array.isArray(data) ? data.length : data.count || 0);
      })
      .catch(() => addNotification(t('Failed to load payments'), 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, page]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  useEffect(() => {
    enrollmentService.getPaymentsSummary()
      .then((res) => setSummary(res.data))
      .catch(() => setSummary(null));
  }, [payments]);

  const openCreate = async () => {
    setForm(EMPTY_FORM);
    setModalOpen(true);
    try {
      const res = await adminService.getEnrollments({ status: 'active', page_size: 100 });
      const data = res.data;
      setEnrollments(Array.isArray(data) ? data : data.results || []);
    } catch (e) {
      addNotification(t('Failed to load enrollments'), 'error');
    }
  };

  const handleEnrollmentChange = (enrollmentId) => {
    const enr = enrollments.find((e) => String(e.id) === String(enrollmentId));
    setForm((prev) => ({
      ...prev,
      enrollment: enrollmentId,
      currency: enr?.module_currency || prev.currency,
      amount: enr?.module_price && Number(enr.module_price) > 0 ? String(enr.module_price) : prev.amount,
    }));
  };

  const save = async () => {
    if (!form.enrollment || !form.amount) {
      addNotification(t('Select a student and enter an amount'), 'error');
      return;
    }
    setSaving(true);
    try {
      await adminService.createPayment(form);
      addNotification(t('Payment recorded'), 'success');
      setModalOpen(false);
      fetchPayments();
    } catch (e) {
      addNotification(getApiErrorMessage(e, t, 'Failed to record payment'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (payment, status) => {
    try {
      const res = await adminService.updatePayment(payment.id, { status });
      setPayments((prev) => prev.map((p) => (p.id === payment.id ? res.data : p)));
      addNotification(t('Payment updated'), 'success');
    } catch (e) {
      addNotification(t('Failed to update payment'), 'error');
    }
  };

  const removePayment = async (payment) => {
    const reason = await promptReason(t('Delete this payment record? This cannot be undone.'));
    if (!reason) return;
    try {
      await adminService.deletePayment(payment.id, reason);
      setPayments((prev) => prev.filter((p) => p.id !== payment.id));
      setCount((current) => Math.max(0, current - 1));
      addNotification(t('Payment deleted'), 'success');
    } catch (e) {
      addNotification(t('Failed to delete payment'), 'error');
    }
  };

  // Totals are grouped by currency rather than summed together — payments
  // can be recorded in USD, EUR, or DZD, and adding those raw numbers would
  // produce a meaningless figure.
  const totalsByStatusAndCurrency = (targetStatus) => {
    const totals = {};
    payments.filter((p) => p.status === targetStatus).forEach((p) => {
      totals[p.currency] = (totals[p.currency] || 0) + Number(p.amount);
    });
    return Object.entries(totals).map(([currency, amount]) => formatPrice(amount, currency)).join(' + ') || formatPrice(0, 'USD');
  };
  const totalCompleted = totalsByStatusAndCurrency('completed');
  const totalPending = totalsByStatusAndCurrency('pending');

  return (
    <div className="flex h-screen bg-surface-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('Payments')} subtitle={t('Record and track student fee payments')} />
        <main className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard label={t('Total Collected')} value={totalCompleted} color="green" />
            <StatCard label={t('Pending')} value={totalPending} color="yellow" />
            <StatCard
              label={t('Potential Revenue')}
              value={summary ? formatPrice(summary.total_potential, 'USD') : formatPrice(0, 'USD')}
              color="purple"
            />
          </div>
          {summary && summary.total_potential_count > 0 && (
            <p className="text-xs text-gray-400 -mt-3">
              {t('{{count}} approved enrollments not yet paid — becomes real revenue once a payment is recorded.', { count: summary.total_potential_count })}
            </p>
          )}

          {summary && (summary.by_teacher.length > 0 || summary.by_module.length > 0) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <h3 className="font-semibold text-gray-900 mb-1">{t('Accounting by Teacher')}</h3>
                <p className="text-xs text-gray-400 mb-3">{t('Click a teacher to see their individual enrollments')}</p>
                <div className="space-y-1">
                  {summary.by_teacher.map((row) => (
                    <button
                      key={row.teacher_id}
                      onClick={() => setAccountingTeacher(row)}
                      className="w-full flex items-center justify-between text-sm rounded-lg px-2 py-2 -mx-2 hover:bg-gray-50 transition text-left"
                    >
                      <span className="text-gray-700">{row.teacher_name}</span>
                      <span className="flex items-center gap-3">
                        <span className="font-semibold text-emerald-700">
                          {formatPrice(row.real_total, 'USD')} <span className="text-gray-400 font-normal">({row.real_count})</span>
                        </span>
                        {row.potential_total > 0 && (
                          <span className="font-semibold text-purple-700">
                            +{formatPrice(row.potential_total, 'USD')} <span className="text-gray-400 font-normal">({row.potential_count})</span>
                          </span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              </Card>
              <Card>
                <h3 className="font-semibold text-gray-900 mb-3">{t('Revenue by Course')}</h3>
                <div className="space-y-2">
                  {summary.by_module.map((row) => (
                    <div key={row.module_id} className="flex items-center justify-between text-sm">
                      <span className="text-gray-700">{row.module_name}</span>
                      <span className="font-semibold text-gray-900">{formatPrice(row.total, row.currency)} <span className="text-gray-400 font-normal">({row.count})</span></span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <Select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="sm:max-w-xs">
              <option value="">{t('All statuses')}</option>
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{t(s)}</option>)}
            </Select>
            <Button onClick={openCreate}>+ {t('Record Payment')}</Button>
          </div>

          {loading ? (
            <div className="p-12 flex justify-center"><Spinner /></div>
          ) : payments.length === 0 ? (
            <Card><EmptyState title={t('No payments recorded yet')} action={<Button onClick={openCreate}>+ {t('Record Payment')}</Button>} /></Card>
          ) : (
            <Card padded={false} className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-3">{t('Student')}</th>
                      <th className="text-left px-4 py-3">{t('Class')}</th>
                      <th className="text-left px-4 py-3">{t('Amount')}</th>
                      <th className="text-left px-4 py-3">{t('Method')}</th>
                      <th className="text-left px-4 py-3">{t('Status')}</th>
                      <th className="text-left px-4 py-3">{t('Date')}</th>
                      <th className="text-right px-4 py-3">{t('Actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {payments.map((p) => (
                      <tr key={p.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{p.student?.first_name} {p.student?.last_name}</div>
                          <div className="text-xs text-gray-400">{p.student?.email}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{p.enrollment_details?.module_name}</td>
                        <td className="px-4 py-3 text-gray-900 font-medium">{formatPrice(p.amount, p.currency)}</td>
                        <td className="px-4 py-3 text-gray-600">{p.payment_method || '—'}</td>
                        <td className="px-4 py-3"><Badge status={p.status} /></td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{new Date(p.created_at).toLocaleDateString()}</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <Select
                            value={p.status}
                            onChange={(e) => updateStatus(p, e.target.value)}
                            className="inline-block w-auto text-xs mr-2"
                          >
                            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{t(s)}</option>)}
                          </Select>
                          <Button size="sm" variant="danger" onClick={() => removePayment(p)}>{t('Delete')}</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination page={page} count={count} pageSize={PAGE_SIZE} onPageChange={setPage} />
            </Card>
          )}
        </main>
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={t('Record Payment')}
        footer={<>
          <Button variant="secondary" onClick={() => setModalOpen(false)}>{t('Cancel')}</Button>
          <Button loading={saving} onClick={save}>{t('Save')}</Button>
        </>}
      >
        <div className="space-y-4">
          <Field label={t('Student / Class')} required>
            <Select value={form.enrollment} onChange={(e) => handleEnrollmentChange(e.target.value)}>
              <option value="">{t('Select an enrollment')}</option>
              {enrollments.map((enr) => (
                <option key={enr.id} value={enr.id}>
                  {enr.student?.first_name} {enr.student?.last_name} — {enr.module_name}
                </option>
              ))}
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('Amount')} required>
              <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            </Field>
            <Field label={t('Currency')}>
              <Select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
                {CURRENCY_OPTIONS.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('Payment Method')}>
              <Select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
                <option value="cash">{t('Cash')}</option>
                <option value="bank_transfer">{t('Bank Transfer')}</option>
                <option value="card">{t('Card')}</option>
                <option value="other">{t('Other')}</option>
              </Select>
            </Field>
            <Field label={t('Status')}>
              <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{t(s)}</option>)}
              </Select>
            </Field>
          </div>
          <Field label={t('Notes')}>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
        </div>
      </Modal>

      <TeacherAccountingModal
        teacher={accountingTeacher}
        onClose={() => setAccountingTeacher(null)}
      />
    </div>
  );
}

export default AdminPayments;
